import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { typeColor } from '../lib/format.js';
import { scanOverlay, base64ZuDatei } from '../lib/scanOverlay.js';

const LIGA = { great: 'Superliga', ultra: 'Hyperliga', master: 'Meisterliga' };

/**
 * Screenshot auswerten.
 *
 * Der Ablauf ist bewusst zweistufig: Auswerten und Anzeigen ändert nichts,
 * gespeichert wird erst auf ausdrücklichen Knopfdruck. So lässt sich ein
 * Pokémon anschauen, ohne den Datenbestand anzufassen.
 */
export default function ScanPage() {
  const [ergebnis, setErgebnis] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ziel, setZiel] = useState('neu');
  const [knopf, setKnopf] = useState(null);   // Zustand des Overlay-Knopfs
  const [laeuftKnopf, setLaeuftKnopf] = useState(false);
  const feld = useRef(null);

  useEffect(() => {
    scanOverlay.zustand().then((z) => {
      setKnopf(z);
      // Den tatsächlichen Zustand übernehmen: Nach einem App-Neustart stand
      // der Schalter sonst auf "aus", obwohl der Knopf noch schwebte — und
      // ein Druck darauf startete einen zweiten, statt ihn zu beenden.
      setLaeuftKnopf(Boolean(z?.laeuft));
    });
  }, []);

  // Aufnahmen vom nativen Knopf entgegennehmen. Der Ablauf ist danach
  // derselbe wie bei einer gewaehlten Datei.
  useEffect(() => {
    let horcher;
    const anmelden = async () => {
      horcher = await scanOverlay.beiAufnahme((e) => {
        if (e.bild) auswerten(base64ZuDatei(e.bild));
      });
    };
    anmelden();
    return () => horcher?.remove?.();
  }, []);

  const knopfUmschalten = async () => {
    setFehler(null);
    try {
      if (laeuftKnopf) {
        await scanOverlay.beenden();
        const alt = localStorage.getItem('scanToken');
        if (alt) {
          await api.scanToken.entwerten(alt).catch(() => {});
          localStorage.removeItem('scanToken');
        }
        setLaeuftKnopf(false);
        return;
      }
      if (!knopf?.overlayErlaubt) {
        const antwort = await scanOverlay.overlayFreigeben();
        setKnopf({ ...knopf, overlayErlaubt: antwort.erlaubt });
        if (!antwort.erlaubt) return;
      }
      // Frisches Token je Einschaltvorgang — so lässt sich ein Gerät
      // gezielt abschalten, ohne die Anmeldung anzufassen.
      const { token } = await api.scanToken.ausstellen(navigator.userAgent);
      localStorage.setItem('scanToken', token);

      const basis = new URL(import.meta.env.VITE_API_BASE || '/api', window.location.href).href
        .replace(/\/$/, '');

      await scanOverlay.starten(token, basis);
      setLaeuftKnopf(true);
    } catch (e) {
      setFehler(e.message ?? String(e));
    }
  };

  const auswerten = async (datei) => {
    if (!datei) return;
    setBusy(true);
    setFehler(null);
    setErgebnis(null);
    setZiel('neu');
    try {
      setErgebnis(await api.scan(datei));
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="hero">
        <span className="hero__tag">Scan</span>
        <h1>Screenshot auswerten</h1>
        <p className="hero__sub">
          Die Bewertung muss geöffnet sein — die drei Balken sind die Quelle
          für die Werte.
        </p>
      </section>

      {knopf?.moeglich && (
        <div className="scan-schalter">
          <div>
            <strong>Knopf über Pokémon GO</strong>
            <div className="card__meta">
              {laeuftKnopf
                ? 'Läuft. Der Knopf lässt sich verschieben; Antippen wertet aus.'
                : knopf.overlayErlaubt
                  ? 'Beim Start bestätigt Android die Bildschirmaufnahme.'
                  : 'Braucht einmalig die Freigabe in den Systemeinstellungen.'}
            </div>
          </div>
          <button
            className={laeuftKnopf ? 'btn btn--ghost' : 'btn btn--primary'}
            onClick={knopfUmschalten}
          >
            {laeuftKnopf ? 'Ausschalten' : 'Einschalten'}
          </button>
        </div>
      )}

      <div className="inline" style={{ marginBottom: 24 }}>
        <input
          ref={feld}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => auswerten(e.target.files?.[0])}
        />
        <button className="btn btn--primary" onClick={() => feld.current?.click()} disabled={busy}>
          {busy ? 'Wertet aus…' : 'Screenshot wählen'}
        </button>
      </div>

      {fehler && <div className="error">{fehler}</div>}

      {ergebnis && !ergebnis.ok && (
        <div className="empty">
          <div className="empty__icon">?</div>
          <p><strong>{ergebnis.fehler}</strong></p>
          {ergebnis.grund === 'keine_balken' && (
            <p>Öffne im Spiel die Bewertung, dann erscheinen die drei Balken.</p>
          )}
          {ergebnis.gelesen && (
            <p className="text-faint">
              Gelesen: {Object.entries(ergebnis.gelesen)
                .map(([k, v]) => k + ': ' + (v || '—')).join(' · ')}
            </p>
          )}
        </div>
      )}

      {ergebnis?.ok && (
        <Ergebnis
          daten={ergebnis}
          ziel={ziel}
          setZiel={setZiel}
          onGespeichert={() => setErgebnis(null)}
        />
      )}
    </>
  );
}

function Ergebnis({ daten, ziel, setZiel, onGespeichert }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState(null);
  const { scan, art, pvp, vorhanden, besser, aufgewertet } = daten;

  const speichern = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await api.scanSpeichern({
        slug: art.slug,
        ivs: scan.ivs,
        level: scan.level,
        cp: scan.wp,
        hp: scan.kp,
        ersetzt: ziel === 'neu' ? null : Number(ziel),
      });
      onGespeichert();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <article className="scan-karte">
        <div className="spread">
          <div>
            <div className="card__title">{art.name}</div>
            <div className="mon__dex">#{String(art.dexNo).padStart(3, '0')}</div>
          </div>
          <div className="scan-karte__iv">
            <span className="scan-karte__zahl">{scan.ivSchnitt} %</span>
            <span className="rang-zeile__wert-label">IV-Schnitt</span>
          </div>
        </div>

        <div className="inline">
          {art.types.map((t) => (
            <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
          ))}
          <span className="badge badge--soft">{scan.ivs.join(' / ')}</span>
          <span className="badge badge--soft">Level {scan.level}</span>
          <span className="badge badge--soft">WP {scan.wp}</span>
          {scan.kp !== null && <span className="badge badge--soft">{scan.kp} KP</span>}
        </div>

        {scan.mehrdeutig && (
          <p className="rang-zeile__belegt">
            Mehrere Level passen zu den gelesenen Werten — das angezeigte ist
            das wahrscheinlichste. Prüfe es im Spiel nach.
          </p>
        )}

        {pvp ? (
          <p className="rang-zeile__einordnung">
            Trainerkampf: {LIGA[pvp.league] ?? pvp.league}, Platz {pvp.rankNo} —
            Stufe {pvp.tier}.{' '}
            <Link to={'/pokemon/' + art.slug}>Zur Bewertung</Link>
          </p>
        ) : (
          <p className="rang-zeile__einordnung text-faint">
            Im Trainerkampf spielt diese Art keine Rolle.
          </p>
        )}
      </article>

      {aufgewertet.length > 0 && (
        <div className="scan-hinweis scan-hinweis--wichtig">
          <strong>Vermutlich dasselbe Pokémon, nur aufgewertet.</strong>
          <p>
            {aufgewertet.map((v) => v.name + ' mit ' + v.cp + ' WP auf Level ' + v.level).join(', ')} hat
            dieselben Werte bei niedrigerem Level. Wähle es unten aus, damit
            kein zweiter Eintrag entsteht.
          </p>
        </div>
      )}

      {besser.length > 0 && (
        <div className="scan-hinweis">
          <strong>Du hast schon besser.</strong>
          <p>
            {besser.length} {besser.length === 1 ? 'Exemplar' : 'Exemplare'} dieser
            Art mit höherem IV-Schnitt — bestes {besser[0].ivAverage} %.
          </p>
        </div>
      )}

      <div className="scan-ziel">
        <strong>Was soll gespeichert werden?</strong>

        <label className="konfig__zeile">
          <input type="radio" checked={ziel === 'neu'} onChange={() => setZiel('neu')} />
          <span>Als neues Exemplar anlegen</span>
        </label>

        {vorhanden.map((v) => (
          <label className="konfig__zeile" key={v.id}>
            <input
              type="radio"
              checked={ziel === String(v.id)}
              onChange={() => setZiel(String(v.id))}
            />
            <span>
              Ersetzt: {v.name} · {v.cp} WP
              {v.ivs && ' · ' + v.ivs.join('/')}
              {v.level !== null && ' · Level ' + v.level}
              {v.note && <span className="konfig__hinweis">{v.note}</span>}
            </span>
          </label>
        ))}

        {fehler && <div className="error">{fehler}</div>}

        <div className="inline">
          <button className="btn btn--primary" onClick={speichern} disabled={busy}>
            {busy ? 'Speichert…' : 'Speichern'}
          </button>
          <span className="text-faint">Bis hierhin wurde nichts geändert.</span>
        </div>
      </div>
    </div>
  );
}
